import { ErrorHandler, inject, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy, RouterModule, Routes } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { HttpClientInMemoryWebApiModule } from 'angular-in-memory-web-api';

import 'hammerjs';
import { MsalInterceptor, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG } from '@azure/msal-angular';
import { IPublicClientApplication } from '@azure/msal-browser';
import { NgbDateParserFormatter, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrModule } from 'ngx-toastr';
import { TranslateModule } from '@ngx-translate/core';
// import { ContextMenuModule } from '@ctrl/ngx-rightclick';

import { CoreModule } from '@core/core.module';
import { CoreCommonModule } from '@core/common.module';
import { CoreSidebarModule, CoreThemeCustomizerModule } from '@core/components';
import { CardSnippetModule } from '@core/components/card-snippet/card-snippet.module';

import { coreConfig } from 'app/app-config';
import { AuthGuard, JwtInterceptor, ErrorInterceptor, TimeoutInterceptor, DEFAULT_TIMEOUT, AiEnvInterceptor } from './helpers';
import { AppComponent } from 'app/app.component';
import { LayoutModule } from 'app/layout/layout.module';
import { ChatAIModule } from 'app/components/chat-ai/chat-ai.module';
import { ContentHeaderModule } from 'app/layout/components/content-header/content-header.module';

import { InsightsService } from '@core/services/Insights.service';
import { initializeMsal, MSALGuardConfigFactory, MSALInstanceFactory, MSALInterceptorConfigFactory } from './msal-config';
import { NgSelectModule } from '@ng-select/ng-select';
import { AppRouteReuseStrategy } from './helpers/app-route-reuse.strategy';
import { NgbCustomDateParserFormatter } from '@core/helpers';
import { ColumnChangesService, DimensionsHelper, ScrollbarHelper } from '@swimlane/ngx-datatable';
import { ErrorHandlerService } from '@core/services/error-handler.service';
import { FileUploadModule } from 'ng2-file-upload';
import { IdleTimeoutService } from '@core/services';
import { BlockUIModule } from 'ng-block-ui';
import { CoreBlockUiComponent } from '@core/components/core-card/core-block-ui/core-block-ui.component';
import { BelivTranslateService } from '@common/services';
import { CoreTranslationService } from '@core/services/translation.service';
import { EdgePanelModule } from 'app/layout/components/edge-panel/edge-panel.module';
import { catchError, retry, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { GlobalUploadComponent } from 'app/components/global-upload/global-upload.component';
export function initTranslations(
  belivTranslateService: BelivTranslateService,
  coreTranslationService: CoreTranslationService
): Promise<any> {
  return belivTranslateService.getAll().pipe(
    tap(resp => {
      resp[0].translations.forEach(trans => coreTranslationService.translate(trans));
    }),
    retry(3),
    catchError(() => of(null))
  ).toPromise();
}

const appRoutes: Routes = [
  {
    path: 'apps',
    loadChildren: () => import('./main/apps/apps.module').then(m => m.AppsModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'dashboards',
    loadChildren: () => import('./main/apps/dashboards/dashboards.module').then(m => m.DashboardsModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'news',
    loadChildren: () => import('./main/apps/news/news.module').then(m => m.NewsModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'search',
    loadChildren: () => import('./main/pages/search/search.module').then(m => m.SearchModule),
    canActivate: [AuthGuard]
  },
  {
    // Sitios externos de los menús marcados como "incrustar"
    path: 'embedded',
    loadChildren: () => import('./main/pages/embedded-site/embedded-site.module').then(m => m.EmbeddedSiteModule)
  },
  {
    path: 'pages',
    loadChildren: () => import('./main/pages/pages.module').then(m => m.PagesModule)
  },
  {
    path: 'teams-config',
    loadChildren: () => import('./teams-config/teams-config.module').then(m => m.TeamsConfigModule)
  },
  {
    path: '',
    redirectTo: '/pages/home',
    pathMatch: 'full'
  },
  {
    path: '**',
    redirectTo: '/pages/miscellaneous/error' //Error 404 - Page not found
  }
];

@NgModule({
  declarations: [
    AppComponent,
    GlobalUploadComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    // Antes de BlockUIModule.forRoot: ChatAIModule también llama a forRoot() y
    // la plantilla de bloqueo global debe seguir siendo la de la app.
    ChatAIModule,
    BlockUIModule.forRoot({ template: CoreBlockUiComponent }),
    RouterModule.forRoot(appRoutes, {
      scrollPositionRestoration: 'enabled', // Add options right here
    }),
    MsalModule,
    NgbModule,
    NgSelectModule,
    ToastrModule.forRoot(),
    TranslateModule.forRoot(),
    CoreModule.forRoot(coreConfig),
    CoreCommonModule,
    CoreSidebarModule,
    CoreThemeCustomizerModule,
    CardSnippetModule,
    LayoutModule,
    EdgePanelModule,
    ContentHeaderModule,
    FileUploadModule
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(() => initializeMsal(inject(MSAL_INSTANCE) as IPublicClientApplication, inject(InsightsService))),
    provideAppInitializer(() => {
      const belivTranslateService = inject(BelivTranslateService);
      const coreTranslationService = inject(CoreTranslationService);
      return initTranslations(belivTranslateService, coreTranslationService);
    }),
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TimeoutInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AiEnvInterceptor, multi: true },
    { provide: DEFAULT_TIMEOUT, useValue: 1500000 },
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
    { provide: ErrorHandler, useClass: ErrorHandlerService},
    { provide: NgbDateParserFormatter, useValue: new NgbCustomDateParserFormatter("DD-MM-YYYY") },
    {provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy},
    MsalService,
    ScrollbarHelper,
    DimensionsHelper,
    ColumnChangesService,
    IdleTimeoutService
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
